import { ReactElement, ReactNode } from 'react'
import { Box, Chip, Typography } from '@mui/material'
import type { ShearAnchorCheckDTO } from '@api/fastAPI.schemas'
import { DenseTable } from '@ui2/structure'
import {
  formatShearDistance,
  SHEAR_ANCHOR_POSITION_LABEL,
} from 'src/components/manager/shearConnections'
import LoadCaseCombinationFormula from '../../Results2DetailPane/components/LoadCaseCombinationFormula'
import {
  checksByLoadCategory,
  checksByPosition,
  decisiveShearCheck,
  formatKilo,
  formatNumber,
  SHEAR_LOAD_CATEGORIES,
  shearLoadCategoryLabel,
  ShearLoadCategory,
} from '../utils'

interface Props {
  checks: ShearAnchorCheckDTO[]
}

/**
 * The design shear flow derivation and its check, transposed with one column per Wind/Erdbeben
 * category — the same quantities the "Übersicht der Bemessungs-Schubflüsse" export lists, plus
 * the chosen shear anchor and the resulting utilization.
 *
 * The derivation is stated once: the same design shear flow has to be carried at the wall's foot
 * and at its head. Only the anchor taken from that end's prioritization list, its spacing, its
 * resistance and the resulting utilization are listed per position.
 */
const ShearFlowCheckTable = ({ checks }: Props): ReactElement => {
  const byCategory = checksByLoadCategory(checks)
  const columns = SHEAR_LOAD_CATEGORIES.filter(
    (category): category is ShearLoadCategory => byCategory[category] != null,
  )

  if (columns.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
        Kein Schubanker-Nachweis verfügbar — bitte Schubverbinder im Systemmanager konfigurieren und
        Ergebnisse berechnen.
      </Typography>
    )
  }

  const decisiveCategory = decisiveShearCheck(checks)?.load_category

  const headers: ReactNode[] = [
    '',
    ...columns.map(category => (
      <Box key={category} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        {shearLoadCategoryLabel[category]}
        {category === decisiveCategory && (
          <Chip label="maßgebend" size="small" sx={{ height: 16, fontSize: 10 }} />
        )}
      </Box>
    )),
  ]

  const cell = (category: ShearLoadCategory, render: (check: ShearAnchorCheckDTO) => ReactNode) => {
    const check = byCategory[category]
    return check ? render(check) : '–'
  }

  const rows: ReactNode[][] = [
    [
      'Lastfallkombination',
      ...columns.map(category =>
        cell(category, check => (
          <LoadCaseCombinationFormula
            key={category}
            lcc={
              check.shear_flow_data.design_horizontal_force
                .load_case_combination as unknown as LoadCaseCombination
            }
          />
        )),
      ),
    ],
    [
      <span key="l">L [m]</span>,
      ...columns.map(category =>
        cell(category, check => formatNumber(check.shear_flow_data.element_length)),
      ),
    ],
    [
      <span key="fhd">
        F<sub>h,d</sub> [kN]
      </span>,
      ...columns.map(category =>
        cell(category, check =>
          formatKilo(check.shear_flow_data.design_horizontal_force.design_force),
        ),
      ),
    ],
    [
      <span key="shd">
        s<sub>h,d</sub> [kN/m]
      </span>,
      ...columns.map(category =>
        cell(category, check => formatKilo(check.shear_flow_data.base_design_shear_flow)),
      ),
    ],
    [
      <span key="sreibung">
        s<sub>Reibung,Rd</sub> [kN/m]
      </span>,
      ...columns.map(category =>
        cell(category, check => formatKilo(check.shear_flow_data.friction_design_shear_flow)),
      ),
    ],
    [
      <Box component="span" key="seffd" sx={{ fontWeight: 700 }}>
        s<sub>eff,d</sub> [kN/m]
      </Box>,
      ...columns.map(category =>
        cell(category, check => (
          <Box component="span" key={category} sx={{ fontWeight: 700 }}>
            {formatKilo(check.design_shear_flow)}
          </Box>
        )),
      ),
    ],
  ]

  for (const { position, checks: checksAtPosition } of checksByPosition(checks)) {
    const positionLabel = SHEAR_ANCHOR_POSITION_LABEL[position]
    const atPosition = checksByLoadCategory(checksAtPosition)
    const cellAtPosition = (
      category: ShearLoadCategory,
      render: (check: ShearAnchorCheckDTO) => ReactNode,
    ) => {
      const check = atPosition[category]
      return check ? render(check) : '–'
    }

    rows.push(
      [
        `Schubanker ${positionLabel}`,
        ...columns.map(category => cellAtPosition(category, check => check.used_anchor.name)),
      ],
      [
        'Abstand [m]',
        ...columns.map(category =>
          cellAtPosition(category, check => formatShearDistance(check.used_anchor.distance)),
        ),
      ],
      [
        <span key={`rd-${position}`}>
          R<sub>d</sub> [kN/m]
        </span>,
        ...columns.map(category =>
          cellAtPosition(category, check => formatKilo(check.design_resistance)),
        ),
      ],
      [
        'Ausnutzung η',
        ...columns.map(category =>
          cellAtPosition(category, check => (
            <Box
              component="span"
              key={`${category}-${position}`}
              sx={{
                fontWeight: 700,
                color: check.utilization > 1 ? 'error.main' : 'success.main',
              }}
            >
              {formatNumber(check.utilization)}
            </Box>
          )),
        ),
      ],
    )
  }

  return (
    <Box sx={{ pb: 1 }}>
      <DenseTable headers={headers} rows={rows} emptyMessage="" />
      <Typography variant="caption" color="text.secondary">
        Der Schubfluss aus horizontalen Lasten wird um die Gleitreibung aus den vertikalen Auflasten
        reduziert (Reibungskoeffizient{' '}
        {formatNumber(checks[0]?.shear_flow_data.friction_coefficient)}). R<sub>d</sub> ist der
        Widerstand des Schubverbinders geteilt durch seinen Abstand.
      </Typography>
    </Box>
  )
}

export default ShearFlowCheckTable
